import type { ReactNode } from 'react'
import { Icon } from '@/components/Icon'
import { links } from '@/utils/link'
import { observer } from 'mobx-react-lite'
import { useRootStore } from '@/contexts/root-store-context'
import { useActivation } from '@/hooks/view-model-hooks'
import { createLayeredContext } from '@/contexts/layered-context'
import {
  type NavItem,
  type NavSection,
  Sidebar,
} from '@/features/shell/global-navigation/Sidebar'
import { ProfileMenuContainer } from '@/features/shell/global-navigation/Sidebar/ProfileMenu/ProfileMenuContainer'

// Used to hide the navigation from within a view.
const {
  Provider: GlobalNavigationHiddenContextProvider,
  useProvide: useProvideGlobalNavigationHiddenValue,
  useValue: useGlobalNavigationHidden,
} = createLayeredContext<boolean | null>(false, {
  excludeNulls: true,
})

export interface SidebarContainerProps {
  floating: boolean
  children?: ReactNode
}

export const SidebarContainer = observer(function SidebarContainer({
  children,
  floating,
}: SidebarContainerProps) {
  return (
    <GlobalNavigationHiddenContextProvider>
      <SidebarRootContainer floating={floating}>
        {children}
      </SidebarRootContainer>
    </GlobalNavigationHiddenContextProvider>
  )
})

/**
 * Allows a child component to hide the global navigation.
 *
 * @param hidden
 */
export function useHideGlobalNavigation(hidden: boolean | null) {
  useProvideGlobalNavigationHiddenValue(hidden, hidden ? 1 : 0)
}

const SidebarRootContainer = observer(function SidebarRootContainer({
  children,
  floating,
}: SidebarContainerProps) {
  const isNavigationHidden = useGlobalNavigationHidden()
  const { shell } = useRootStore()
  const vm = shell.internal.globalNavigationVm
  useActivation(vm)

  const sections: NavSection[] = [
    {
      key: 'main',
      collapsed: vm.isSectionCollapsed('main'),
      items: [
        {
          kind: 'page',
          name: 'NotificationsToggle',
          path: links.notifications(),
          title: 'Notifications',
          icon: <Icon name="notifications" />,
          badgeCount: vm.notificationCount,
          badgePill: true,
        },
        {
          kind: 'page',
          name: 'dashboard',
          path: links.firmDashboard(),
          hidden: !vm.canViewFirmDashboard,
          exact: true,
          icon: <Icon name="dashboard" />,
          title: 'Dashboard',
        },
        {
          kind: 'page',
          name: 'opportunities',
          path: links.opportunities(),
          hidden: !vm.canViewOpportunities,
          icon: <Icon name="opportunities" />,
          title: 'Opportunities',
          badgeCount: vm.opportunityCount,
        },
        {
          kind: 'page',
          name: 'jobs',
          path: links.jobs(),
          hidden: !vm.canViewJobs,
          icon: <Icon name="jobs" />,
          title: 'My jobs',
          badgeCount: vm.unreadMessagesCount,
        },
        {
          kind: 'page',
          name: 'scoping-jobs',
          path: links.scopingJobs(),
          hidden: !vm.canViewScopingJobs,
          icon: <Icon name="ocr" />,
          title: 'Scoping jobs',
        },
        {
          kind: 'action',
          name: 'create-new-job-button',
          title: 'New job',
          hidden: !vm.canDraftJob,
          icon: <Icon name="add" />,
          onPress: vm.createJob,
        },
      ],
    },
    {
      key: 'workspace',
      collapsed: vm.isSectionCollapsed('workspace'),
      title: 'Workspace',
      items: [
        {
          kind: 'page',
          name: 'users',
          path: links.userManagement(),
          hidden: !vm.canAccessUserManagement,
          icon: <Icon name="users" />,
          title: 'Users',
        },
        {
          kind: 'page',
          name: 'clients',
          path: links.clientUserManagement(),
          hidden: !vm.canAccessClientUserManagement,
          icon: <Icon name="users" />,
          title: 'Clients',
        },
        {
          kind: 'page',
          name: 'teams',
          path: links.teams(),
          hidden: !vm.canViewTeams,
          icon: <Icon name="teams" />,
          title: 'Teams',
        },
        {
          kind: 'page',
          name: 'legends',
          path: links.legends(),
          hidden: !vm.canViewLegends,
          icon: <Icon name="legends" />,
          title: 'Legends',
        },
        {
          kind: 'page',
          name: 'documents-root',
          path: links.documents(),
          hidden: !vm.canViewDocuments,
          icon: <Icon name="documents" />,
          title: 'Documents Center',
        },
        {
          kind: 'page',
          name: 'pro-payouts',
          path: links.proPayouts(),
          hidden: !vm.canViewProPayouts,
          icon: <Icon name="payout" />,
          title: 'Payouts',
        },
        ...vm.customLinks.map((item): NavItem => ({
          kind: 'external',
          name: `custom-link-${item.label}`,
          href: item.url,
          icon: <Icon name="link" />,
          title: item.label,
        })),
        {
          kind: 'action',
          name: 'WorkspaceSelectorToggle',
          title: 'Switch workspace',
          hidden: !vm.canSwitchWorkspaces,
          active: vm.isWorkspaceSelectorOpen,
          icon: <Icon name="switch-workspaces" />,
          onPress: vm.toggleWorkspaceSelector,
        },
      ],
    },
    {
      key: 'admin',
      collapsed: vm.isSectionCollapsed('admin'),
      title: 'Admin',
      hidden: !vm.canSeeAdminView,
      items: [
        {
          kind: 'page',
          name: 'admin-change-requests-page-button',
          path: links.adminChangeRequests(),
          hidden: !vm.canManageJobScopeChangeRequest,
          icon: <Icon name="pending-approval" />,
          title: 'Change requests',
        },
        {
          kind: 'page',
          name: 'admin-bookkeeping-button',
          path: links.adminBookkeepingManager(),
          hidden: !vm.canManageBookkeeping,
          icon: <Icon name="invoices" />,
          title: 'Bookkeeping',
        },
        {
          kind: 'page',
          name: 'admin-staffing-manager-button',
          path: links.adminStaffingManager(),
          hidden: !vm.canSeeStaffingManager,
          icon: <Icon name="skills" />,
          title: 'Staffing',
        },
        {
          kind: 'page',
          name: 'admin-bundle-manager-button',
          path: links.adminBundleManager(),
          hidden: !vm.canManageServices,
          icon: <Icon name="legends" />,
          title: 'Bundles',
        },
        {
          kind: 'page',
          name: 'admin-page-button',
          path: links.adminServiceManager(),
          hidden: !vm.canManageServices,
          icon: <Icon name="settings-nav" />,
          title: 'Services',
        },
        {
          kind: 'page',
          name: 'admin-payouts-button',
          path: links.adminPayouts(),
          hidden: !vm.canViewPayoutsAdmin,
          icon: <Icon name="total-paid" />,
          title: 'Payouts',
        },
        {
          kind: 'page',
          name: 'admin-account-merge-requests-button',
          path: links.adminAccountMergeRequests(),
          hidden: !vm.canManageAccountMergeRequests,
          icon: <Icon name="roles" />,
          title: 'Account merges',
        },
        {
          kind: 'action',
          name: 'go-to-hq',
          title: 'Go to HQ',
          hidden: !vm.canGoToHq,
          icon: <Icon name="link" />,
          onPress: vm.goToHq,
        },
      ],
    },
  ]

  return (
    <Sidebar
      floating={floating}
      isRailHidden={!!isNavigationHidden}
      logoIconWhite={shell.shellInfo.invertedIconUrl}
      workspaceName={shell.shellInfo.name}
      sections={sections}
      accountAvatar={{
        avatar: vm.member.avatar,
        avatarFallback: vm.member.avatarFallback,
        displayName: vm.member.displayName,
      }}
      accountLabel={vm.member.displayName || vm.member.email}
      onAccountPress={vm.profileMenu.show}
      profileMenu={<ProfileMenuContainer vm={vm} />}
      onToggleSectionCollapsed={vm.toggleSectionCollapsed}
    >
      {children}
    </Sidebar>
  )
})
